// post-auth-redirect.ts — workspace-members cycle (THI-110 Task 5). `/invite/:token`
// (`InviteAccept.tsx`) needs an unauthenticated visitor's Sign in / Sign up click to return
// them to the SAME invite afterward, but neither auth page reads a redirect target today (spec
// task-5-brief.md step 2: "follow whatever redirect-after-auth mechanism the sign-in page
// supports; if none exists, store the pending path in sessionStorage and honor it after the
// sign-in hard-navigation — smallest working mechanism"). This is that mechanism: one
// `sessionStorage` key, written by `InviteAccept.tsx` right before it renders the Sign in/Sign
// up links (also carried as `?next=` on those links themselves, for whenever a real redirect-
// after-auth param lands and this can be retired), consumed once by `SignIn.tsx` — and, best-
// effort, by `VerifyEmail.tsx` for the sign-up path — immediately before their own post-auth
// `location.assign`.
//
// `sessionStorage`, not `localStorage`: a pending invite is specific to the CURRENT tab's
// journey (open the invite link, bounce to sign-in, come back) — it must not survive into an
// unrelated tab or leak into a later, unrelated sign-in days later. Known gap: it does NOT
// survive the sign-up path's email-verification hop into a DIFFERENT tab (the overwhelmingly
// common case for a real email client) — `sessionStorage` is per-tab by definition. Fixing that
// would need a real `?next=`-carrying `callbackURL` threaded through better-auth's email-
// verification link, out of scope for this "smallest working mechanism" cycle.
const PENDING_REDIRECT_KEY = 'embent:pendingRedirect';

export function setPendingRedirect(path: string): void {
  sessionStorage.setItem(PENDING_REDIRECT_KEY, path);
}

/** Reads AND clears the pending path in one call — a redirect must only ever fire once, the
 * same way a one-time token would. `null` when nothing (or nothing still relevant) was
 * pending. */
export function consumePendingRedirect(): string | null {
  const path = sessionStorage.getItem(PENDING_REDIRECT_KEY);
  if (path === null) return null;
  sessionStorage.removeItem(PENDING_REDIRECT_KEY);
  return path;
}
